import { useCallback, useContext, type FunctionComponent } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { asMoney, forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { BillingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { getOrganizationBillingUrl } from '@trello/urls';

import { useHasReverseTrialExperience } from 'app/src/components/FreeTrial';
import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuotes, ChangeStepFn } from '../types';

import styles from './PlanComparisonStandardHeaderCell.less';

export interface PlanComparisonStandardHeaderCellProps {
  teamId: string;
  teamName: string;
  isMonthly: boolean;
  prices?: BillingQuotes['standardPrices'];
  changeStep: ChangeStepFn;
  isInPPCohort?: boolean;
}

const format = forTemplate('billing_page_one');

export const PlanComparisonStandardHeaderCell: FunctionComponent<
  PlanComparisonStandardHeaderCellProps
> = ({ teamId, teamName, isMonthly, prices, changeStep, isInPPCohort }) => {
  const {
    onClickGetStandard,
    isModal,
    hasEndOfTrialFrictionExperience,
    onChooseStandard,
  } = useContext(PlanComparisonContext);

  const onChooseStandardWithAnalytics = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'chooseStandardButton',
      source: 'trelloPlanPricingModal',
      attributes: {
        viaEndOfTrial: true,
      },
    });
    onChooseStandard?.();
  }, [onChooseStandard]);

  const onClickButtonInModal = hasEndOfTrialFrictionExperience
    ? onChooseStandardWithAnalytics
    : onClickGetStandard;

  const productCode = isMonthly
    ? prices?.monthly.productCode
    : prices?.annual.productCode;
  const pricePerUser = isMonthly
    ? prices?.monthly.subTotalPerUser
    : prices?.annual.subTotalPerUser;
  const frequency = format(isMonthly ? 'per-month' : 'per-year');

  const sendClickUpgradeButtonEvent = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'upgradeToStandardButton',
      source: 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
    });
  };

  const onClickUpgradeButton = () => {
    changeStep({
      selectedProductCode: productCode || null,
      step: 'billing',
      analyticsCb: sendClickUpgradeButtonEvent,
    });
  };

  const isReverseTrial = useHasReverseTrialExperience();

  return (
    <th
      className={styles.planCell}
      data-value={format('best-value')}
      data-testid={getTestId<BillingIds>('plan-comparison-cell-standard')}
    >
      <h3 className={styles.name}>{format('standard-name')}</h3>
      <p className={styles.info}>
        {isInPPCohort
          ? format('standard-description-personal-productivity')
          : format('standard-description-repackaging-gtm')}
      </p>
      <span className={styles.price}>
        {format('amount-usd', {
          amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
        }).replace(/\s/g, ' ')}
      </span>
      <p className={styles.frequency}>
        {format('per-user')}
        <br />
        {frequency}
      </p>
      {isReverseTrial ? (
        <Button
          href={getOrganizationBillingUrl(teamName, { selected: 'standard' })}
        >
          {format('upgrade-now')}
        </Button>
      ) : (
        <Button
          key={isModal ? `modal-standard` : `upgrade-standard`}
          onClick={isModal ? onClickButtonInModal : onClickUpgradeButton}
          testId={
            isModal
              ? getTestId<BillingIds>('get-standard')
              : getTestId<BillingIds>('upgrade-team-standard-button')
          }
        >
          {isModal ? format('get-standard') : format('upgrade-now')}
        </Button>
      )}
    </th>
  );
};
